import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import SelectMe from 'react-select-me';

import { formatCurrency, formatValue } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import { useDvfYears } from '../../../hooks/useDvfYears';
import type { RootState } from '../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable/MUITable.module';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';

type Filters = {
  ventesMin: number | null;
  ventesMax: number | null;
  caMin: number | null;
  caMax: number | null;
  commMin: number | null;
  commMax: number | null;
  avgSaleMin: number | null;
  scisMin: number | null;
  transactionnaire: string | null;
};

type CoproSalesRow = {
  id: number;
  immatriculation: string;
  name: string;
  address: string;
  cp: string;
  city: string;
  salesAmount: number;
  nbLots: number;
  nbSales: number;
  m2ForNbSales: number | null;
  salesAvgForM2: number | null;
};

const TRANSACTIONNAIRE_FONCTION_TYPE = 3; // see FonctionTypeUserEnum.TRANSACTIONNAIRE

interface Props {
  onOpenBoitageDocument: () => void;
}

function BoitageLots({ onOpenBoitageDocument }: Props) {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  const { yearsOptions } = useDvfYears({ startYear: 2014 });

  const [rows, setRows] = useState<CoproSalesRow[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sciCountByCopro, setSciCountByCopro] = useState<Record<number, number>>({});
  const [transactionnaireByCopro, setTransactionnaireByCopro] = useState<Record<number, string[]>>({});
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>({ ventesMin: null, ventesMax: null, caMin: null, caMax: null, commMin: null, commMax: null, avgSaleMin: null, scisMin: null, transactionnaire: null });
  const [commissionRate] = useState<number>(company?.commission ?? 5);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);

  const headers = [
    { title: 'Immatriculation' },
    { title: 'Nom' },
    { title: 'Adresse' },
    { title: 'Ville' },
    { title: 'Montant total des ventes' },
    { title: 'Commission' },
    { title: 'Nb lots hab / local pro' },
    { title: 'Nb ventes' },
    { title: 'm2 moyen / vente' },
    { title: 'Prix moyen / m2' },
    { title: 'Nb SCI' },
    { title: 'Transactionnaire' },
    { title: 'Actions' },
  ];

  const fetchRows = async() => {
    if (!company?.id || !jwt || selectedYears.length === 0) {return;}
    setStatus('PENDING');
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      selectedYears.forEach((y) => params.append('years[]', String(y)));
      const resp = await fetch(`/api/companies/${company.id}/stats/copros/sales/years?${params.toString()}`, {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      const results = data.results || data;
      interface ApiSalesRow {
        id: number;
        immatriculation?: string;
        name?: string;
        address?: string;
        cp?: string;
        city?: string;
        salesAmount?: number;
        nbLots?: number;
        nbSales?: number;
        m2ForNbSales?: number | null;
        salesAvgForM2?: number | null;
      }
      const mapped: CoproSalesRow[] = (Array.isArray(results) ? results : []).map((r: ApiSalesRow) => ({
        id: r.id,
        immatriculation: r.immatriculation,
        name: r.name,
        address: r.address,
        cp: r.cp,
        city: r.city,
        salesAmount: Number(r.salesAmount || 0),
        nbLots: Number(r.nbLots || 0),
        nbSales: Number(r.nbSales || 0),
        m2ForNbSales: r.m2ForNbSales != null ? Number(r.m2ForNbSales) : null,
        salesAvgForM2: r.salesAvgForM2 != null ? Number(r.salesAvgForM2) : null,
      }));
      setRows(mapped);
      setStatus('SUCCESS');
    } catch (e) {
       
      console.error('Error fetching sales rows :', e);
      setStatus('ERROR');
      setErrorMessage('Une erreur est survenue lors du chargement des données.');
    }
  };

  const fetchSciCounts = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/copros/scis/stats`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data: Array<{ id: number; scis: { total: number } }> = await resp.json();
      const map: Record<number, number> = {};
      data.forEach((c) => { map[c.id] = c.scis?.total ?? 0; });
      setSciCountByCopro(map);
    } catch (e) {
       
      console.error('Error fetching SCI counts :', e);
    }
  };

  const fetchTransactionnaires = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/validated-copros/attributions`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      const map: Record<number, string[]> = {};
      interface AttributionEntry {
        fonctionType?: number;
        copro?: { id: number };
        user?: { firstname: string; lastname?: string };
      }
      (data?.results || []).forEach((a: AttributionEntry) => {
        if (a?.fonctionType === TRANSACTIONNAIRE_FONCTION_TYPE && a?.copro?.id && a?.user) {
          const name = `${a.user.firstname} ${a.user.lastname ? a.user.lastname.toUpperCase() : ''}`.trim();
          if (!map[a.copro.id]) { map[a.copro.id] = []; }
          map[a.copro.id].push(name);
        }
      });
      setTransactionnaireByCopro(map);
    } catch (e) {
       
      console.error('Error fetching attributions :', e);
    }
  };

  useEffect(() => {
    if (yearsOptions.length > 0 && selectedYears.length === 0) {
      const firstTwo = yearsOptions.slice(0, 2).map((o) => Number(o.value));
      setSelectedYears(firstTwo);
    }
  }, [yearsOptions, selectedYears.length]);

  useEffect(() => { fetchRows(); fetchSciCounts(); fetchTransactionnaires(); }, [company?.id, jwt, selectedYears.join(',')]);

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    const rate = commissionRate / 100;
    return rows.filter((r) => {
      const commission = r.salesAmount * rate;
      const txNames = transactionnaireByCopro[r.id] || [];
      const sciCount = sciCountByCopro[r.id] ?? 0;
      const avgSale = r.nbSales > 0 ? (r.salesAmount / r.nbSales) : 0;
      const basic = `${r.immatriculation} ${r.name} ${r.address} ${r.city} ${r.cp}`.toLowerCase();
      if (q && !basic.includes(q)) { return false; }
      if (filters.ventesMin != null && r.nbSales < filters.ventesMin) { return false; }
      if (filters.ventesMax != null && r.nbSales > filters.ventesMax) { return false; }
      if (filters.caMin != null && r.salesAmount < filters.caMin) { return false; }
      if (filters.caMax != null && r.salesAmount > filters.caMax) { return false; }
      if (filters.commMin != null && commission < filters.commMin) { return false; }
      if (filters.commMax != null && commission > filters.commMax) { return false; }
      if (filters.avgSaleMin != null && avgSale < filters.avgSaleMin) { return false; }
      if (filters.scisMin != null && sciCount < filters.scisMin) { return false; }
      if (filters.transactionnaire && !txNames.includes(filters.transactionnaire)) { return false; }
      return true;
    });
  }, [rows, search, filters, commissionRate, transactionnaireByCopro, sciCountByCopro]);

  const dataForTable = useMemo(() => {
    const rate = commissionRate / 100;
    return filteredRows.map((r) => ({
      'Immatriculation': r.immatriculation,
      'Nom': (
        <div className="flex items-center gap-2">
          <RedirectionCoproButtons coproId={r.id} />
          <span className="font-semibold">{r.name}</span>
        </div>
      ),
      'Adresse': r.address,
      'Ville': `${r.city} - ${r.cp}`,
      'Montant total des ventes': formatCurrency(r.salesAmount),
      'Commission': formatCurrency(r.salesAmount * rate),
      'Nb lots hab / local pro': r.nbLots,
      'Nb ventes': r.nbSales,
      'm2 moyen / vente': r.m2ForNbSales != null ? formatValue(r.m2ForNbSales) : '-',
      'Prix moyen / m2': r.salesAvgForM2 != null ? formatCurrency(r.salesAvgForM2) : '-',
      'Nb SCI': sciCountByCopro[r.id] ?? '-',
      'Transactionnaire': (
        <span className="text-xs font-medium text-purple-600">{(transactionnaireByCopro[r.id] || []).join(', ')}</span>
      ),
      'Actions': (
        <button
          className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
          onClick={onOpenBoitageDocument}
        >
          Voir les modèles pour le boîtage sur le bien à vendre
        </button>
      ),
    }));
  }, [filteredRows, commissionRate, sciCountByCopro, transactionnaireByCopro, onOpenBoitageDocument]);

  const resetFilters = () => {
    setFilters({ ventesMin: null, ventesMax: null, caMin: null, caMax: null, commMin: null, commMax: null, avgSaleMin: null, scisMin: null, transactionnaire: null });
  };

  return (
    <div className="bg-white">
      {/* Inline filters (parity with detailed list) */}
      <div className="mb-3 rounded-lg border bg-white shadow-sm">
        <div className="p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-6">
            <div className="flex items-start gap-2">
              <label className="mt-2 text-sm font-medium text-slate-600"><span role="img" aria-label="magnifying glass">🔍</span> Rechercher :</label>
              <input className="w-64 rounded border px-3 py-2 text-sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, ville, immatriculation..." />
            </div>
            <div className="flex items-start gap-2">
              <label className="mt-2 text-sm font-medium text-slate-600"><span role="img" aria-label="calendar">📅</span> Années :</label>
              <div className="min-w-[220px]">
                <SelectMe
                  id="years-select"
                  name="years"
                  value={selectedYears.map((y) => ({ value: String(y), label: String(y) }))}
                  onChange={(selectedOptions) => setSelectedYears(selectedOptions.map((o) => Number(o.value)))}
                  options={yearsOptions}
                  multiple
                  closeOnSelect={false}
                  listPosition="bottom"
                  placeholder="Sélectionnez une ou plusieurs années"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 rounded border p-4" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderColor: '#3b82f6' }}>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="chart">📊</span> Nb ventes:</label>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.ventesMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, ventesMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min" />
                <span>à</span>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.ventesMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, ventesMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="emoji">💰</span> CA:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.caMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, caMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
                <span>à</span>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.caMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, caMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="target">🎯</span> Commission:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.commMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, commMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
                <span>à</span>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.commMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, commMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="person">👤</span> Transactionnaire:</label>
                <select className="w-40 rounded border px-2 py-1 text-sm" value={filters.transactionnaire ?? ''} onChange={(e) => setFilters((f) => ({ ...f, transactionnaire: e.target.value || null }))}>
                  <option value="">Tous</option>
                  {[...new Set(Object.values(transactionnaireByCopro).flat().filter(Boolean))].map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="emoji">🏷️</span> Prix moyen/vente:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.avgSaleMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, avgSaleMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="factory">🏢</span> Nb SCI min:</label>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.scisMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, scisMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min" />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button className="rounded bg-slate-500 px-3 py-1.5 text-sm text-white hover:bg-slate-600" onClick={resetFilters}><span role="img" aria-label="refresh">🔄</span> Réinitialiser</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {status === 'ERROR' && (
        <div className="p-6"><Badge mode="error" text={errorMessage || 'Erreur'} /></div>
      )}
      {status === 'PENDING' && (
        <div className="p-6"><Badge mode="warning" text="Chargement..." /></div>
      )}
      {status === 'SUCCESS' && (
        <div className="p-4">
          <MUITable
            data={dataForTable}
            headers={headers}
            tableClass="min-w-full bg-white border text-xs"
            sortableColumns={['Nb ventes', 'Montant total des ventes', 'Commission', 'Nb lots hab / local pro']}
            defaultSortKey="Nb ventes"
            defaultSortDirection="desc"
          />
        </div>
      )}
    </div>
  );
}

export default BoitageLots;


